import React, {FC} from 'react';

import {Plural, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {SyncJobModel} from '../../../../../libs/shared/core/src/models/api/jobModel';
import {isJobRunning} from '../../../../../libs/shared/core/src/models/helpers/jobHelpers';
import {CrmSyncModel, CrmSyncSourcesEnum} from '../../../../models/api/crmSyncModel';
import {ErrorBanner} from '../../../common/banners/errorBanner';

/*
 * Props.
 */

interface CrmSyncErrorsBannerProps {
  className?: string;
  crmSync?: CrmSyncModel;
  shouldDisplayCrmSyncName?: boolean;
  latestJob?: SyncJobModel;
  onViewErrorsClick: () => void;
  onCloseClick?: () => void;
  isDisabled?: boolean;
}

/*
 * Component.
 */

export const CrmSyncErrorsBanner: FC<CrmSyncErrorsBannerProps> = (props) => {
  const {
    className,
    crmSync,
    shouldDisplayCrmSyncName,
    latestJob,
    isDisabled = false,
    onViewErrorsClick,
    onCloseClick,
  } = props;

  if (
    !crmSync ||
    crmSync.errors.length === 0 ||
    !latestJob ||
    isJobRunning(latestJob) ||
    !latestJob.metadata?.syncFailed
  ) {
    return null;
  }
  return (
    <ErrorBanner
      description={renderCrmSyncErrorsText(crmSync, latestJob.metadata.syncFailed, shouldDisplayCrmSyncName)}
      onButtonClick={onViewErrorsClick}
      buttonContent={<Trans id="crm-sync-errors-banner-view-errors-button">VIEW ERRORS</Trans>}
      isDisabled={isDisabled}
      onCloseClick={onCloseClick}
      className={className}
    />
  );
};

/*
 * Helpers.
 */

function renderCrmSyncErrorsText(
  crmSync: CrmSyncModel,
  totalFailed: number,
  shouldDisplayCrmSyncName?: boolean,
) {
  if (!shouldDisplayCrmSyncName) {
    return renderDefaultCrmSyncErrors(totalFailed);
  }
  switch (crmSync.source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <Plural
          id="crm-sync-errors-banner-salesforce-total-errors"
          value={totalFailed}
          one="# Salesforce sync error found"
          other="# Salesforce sync errors found"
        />
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <Plural
          id="crm-sync-errors-banner-hubspot-total-errors"
          value={totalFailed}
          one="# Hubspot sync error found"
          other="# Hubspot sync errors found"
        />
      );
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <Plural
          id="crm-sync-errors-banner-microsoft-dynamics-total-errors"
          value={totalFailed}
          one="# Dynamics 365 sync error found"
          other="# Dynamics 365 sync errors found"
        />
      );
    case CrmSyncSourcesEnum.OUTLOOK_DIRECTORY:
      return (
        <Plural
          id="crm-sync-errors-banner-outlook-total-errors"
          value={totalFailed}
          one="# Outlook directory sync error found"
          other="# Outlook directory sync errors found"
        />
      );
    default:
      return renderDefaultCrmSyncErrors(totalFailed);
  }
}

function renderDefaultCrmSyncErrors(errorCount: number) {
  return (
    <Plural
      id="crm-sync-errors-banner-total-errors"
      value={errorCount}
      one="# sync error found"
      other="# sync errors found"
    />
  );
}
